import type { ReactNode } from "react";
import styled from "@emotion/styled";
import { TX_FILTER_DIMENSIONS, TX_FILTER_GROUPS } from "@clustec/common/api-schemas";
import { theme } from "../../lib/theme";
import { media } from "../../lib/breakpoints";
import type { TxFacetsResponse, FeePayerStat } from "../../lib/api";
import { Select } from "../ui";
import { CollapsibleSection } from "../CollapsibleSection";
import { FilterKnob } from "./FilterKnob";
import { FeePayerCombobox } from "./FeePayerCombobox";
import { clearFilter, type TxFilterState, type TxRange } from "../../lib/tx-filters";

const CategoricalRow = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
  margin-bottom: ${theme.spacing.md};
`;

export const GroupTitle = styled.h3`
  margin: ${theme.spacing.md} 0 ${theme.spacing.sm};
  font-size: ${theme.fontSize.xs};
  font-weight: 600;
  color: ${theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

export const KnobGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: ${theme.spacing.md};

  ${media.sm} {
    gap: ${theme.spacing.sm};
  }
`;

export interface FilterPanelProps {
  facets: TxFacetsResponse | undefined;
  /** Facets recomputed from the filtered set — replace the network-wide ones while filters are active. */
  filteredFacets?: TxFacetsResponse;
  filters: TxFilterState;
  onChange: (next: TxFilterState) => void;
  /** Top fee payers by tx count, for the fee payer select. */
  feePayers?: FeePayerStat[];
  /** Headline shown in the section header while collapsed (active count, match %). */
  summary?: ReactNode;
}

export function FilterPanel({ facets, filteredFacets, filters, onChange, feePayers, summary }: FilterPanelProps) {
  const facetByKey = new Map(facets?.facets.map((f) => [f.key, f]));
  const filteredByKey = new Map(filteredFacets?.facets.map((f) => [f.key, f]));

  const setRange = (key: (typeof TX_FILTER_DIMENSIONS)[number]["key"], range: TxRange | null) => {
    if (range === null) {
      onChange(clearFilter(filters, key));
    } else {
      onChange({ ...filters, ranges: { ...filters.ranges, [key]: range } });
    }
  };

  return (
    <CollapsibleSection id="tx-filter-panel" title="Filters" defaultOpen={false} summary={summary}>
      <CategoricalRow>
        <Select
          value={filters.status ?? ""}
          onChange={(e) => onChange({ ...filters, status: e.target.value || undefined })}
        >
          <option value="">All statuses</option>
          <option value="dropped">Dropped</option>
          <option value="pending">Pending</option>
          <option value="proposed">Proposed</option>
          <option value="checkpointed">Checkpointed</option>
          <option value="proven">Proven</option>
          <option value="finalized">Finalized</option>
        </Select>
        <Select
          value={filters.executionResult ?? ""}
          onChange={(e) => onChange({ ...filters, executionResult: e.target.value || undefined })}
        >
          <option value="">Any execution result</option>
          <option value="success">Success</option>
          <option value="app_logic_reverted">App logic reverted</option>
          <option value="teardown_reverted">Teardown reverted</option>
          <option value="both_reverted">Both reverted</option>
        </Select>
        <Select
          value={filters.hasTeardown ?? ""}
          onChange={(e) =>
            onChange({
              ...filters,
              hasTeardown: e.target.value === "true" || e.target.value === "false" ? e.target.value : undefined,
            })
          }
        >
          <option value="">Teardown: any</option>
          <option value="true">Has teardown</option>
          <option value="false">No teardown</option>
        </Select>
        <FeePayerCombobox
          value={filters.feePayer}
          feePayers={feePayers}
          onChange={(address) => onChange({ ...filters, feePayer: address })}
        />
      </CategoricalRow>

      {TX_FILTER_GROUPS.map((group) => (
        <div key={group}>
          <GroupTitle>{group}</GroupTitle>
          <KnobGrid>
            {TX_FILTER_DIMENSIONS.filter((d) => d.group === group).map((d) => (
              <FilterKnob
                key={d.key}
                dim={d}
                facet={filteredFacets ? filteredByKey.get(d.key) : facetByKey.get(d.key)}
                filtered={filteredFacets !== undefined}
                range={filters.ranges[d.key]}
                onChange={(range) => setRange(d.key, range)}
              />
            ))}
          </KnobGrid>
        </div>
      ))}
    </CollapsibleSection>
  );
}
